import { useEffect, useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { CalendarIcon } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";

// Phase 3 (F6): adviser "Commentary" surface — the PUBLIC blog tier only.
//
// Reads exactly the same published, 'public'-typed blog_posts the marketing site exposes.
// The adviser content-entitlement policy (m31) mirrors this predicate on the backend, so
// the wall is RLS; this page is a thin, entitlement-aligned reader.

interface AdviserBlogPost {
  id: string;
  title: string;
  slug: string;
  excerpt: string | null;
  published_at: string | null;
  category: string | null;
}

export default function AdviserBlog() {
  const [posts, setPosts] = useState<AdviserBlogPost[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data, error } = await supabase
          .from("blog_posts")
          .select("id, title, slug, excerpt, published_at, category")
          .eq("status", "published")
          .contains("blog_types", ["public"])
          .order("published_at", { ascending: false });

        if (!cancelled) {
          if (error) {
            setPosts([]);
          } else {
            setPosts((data as AdviserBlogPost[]) || []);
          }
        }
      } catch {
        if (!cancelled) setPosts([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold">Market Commentary</h2>
        <p className="text-muted-foreground">Our latest published commentary and market views.</p>
      </div>

      {loading ? (
        <div className="text-center py-12 text-muted-foreground">Loading commentary...</div>
      ) : posts.length === 0 ? (
        <div className="text-center py-12 text-muted-foreground">No commentary available yet.</div>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2">
          {posts.map((post) => (
            <Card key={post.id}>
              <CardHeader>
                <div className="flex items-center gap-2 flex-wrap">
                  {post.category && <Badge variant="outline">{post.category}</Badge>}
                  {post.published_at && (
                    <span className="inline-flex items-center gap-1 text-xs text-muted-foreground">
                      <CalendarIcon className="h-3 w-3" />
                      {new Date(post.published_at).toLocaleDateString()}
                    </span>
                  )}
                </div>
                <CardTitle className="text-lg">{post.title}</CardTitle>
                {post.excerpt && <CardDescription>{post.excerpt}</CardDescription>}
              </CardHeader>
              <CardContent>
                <a
                  href={`/blog/${post.slug}`}
                  className="text-sm text-primary underline hover:no-underline"
                >
                  Read more →
                </a>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
